/**
 * world/zones.ts — The World Zone System
 *
 * Defines every district, hamlet, and wilderness of the Hearth world as a
 * connected graph. Zones have danger levels, mood signatures, ambient weather,
 * and unlock requirements. Travel between them takes real time and carries
 * narrative weight — you don't just teleport, you depart and arrive.
 *
 * The world is not a menu. It's a place.
 */

// ─── Zone Definition ──────────────────────────────────────────────────────────

export interface WorldZone {
  id: string;
  name: string;
  description: string;
  type:
    | "residential"
    | "commercial"
    | "nature"
    | "harbor"
    | "mountain"
    | "underground"
    | "skyline"
    | "forest"
    | "crafting"
    | "social"
    | "danger"
    | "mystery"
    | "training";
  dangerLevel: 0 | 1 | 2 | 3;
  requiredLevel: number;
  /** Position on the world map — normalized 0–1 */
  mapX: number;
  mapY: number;
  /** Connections to other zones by ID */
  connections: string[];
  /** Hex color for the map node */
  primaryColor: string;
  /** Hex color for map accents */
  secondaryColor: string;
  /** Short identifier used for procedural icon drawing */
  icon: string;
  /** Default ambient weather */
  defaultWeather: "clear" | "rain" | "snow" | "fog" | "wind" | "storm";
  /** Mood profile — all values 0–1 */
  defaultMood: {
    warmth: number;
    energy: number;
    mystery: number;
    nature: number;
  };
  /** Whether the player has unlocked fast-travel to this zone */
  waystoneDiscovered: boolean;
  /** How much of this zone the player has explored — 0–1 */
  explored: number;
}

export interface TravelRoute {
  from: string;
  to: string;
  /** Seconds of real time for the journey */
  travelTime: number;
  dangerLevel: number;
  /** Flavor text describing the path */
  description: string;
}

export interface WorldState {
  zones: WorldZone[];
  currentZoneId: string;
  travelState: {
    active: boolean;
    fromZoneId: string | null;
    toZoneId: string | null;
    /** 0–1 journey completion */
    progress: number;
    startedAt: number;
    /** Duration in ms */
    duration: number;
    phase: "departing" | "traveling" | "arriving";
  };
}

// ─── Zone Definitions ─────────────────────────────────────────────────────────

const ZONE_DEFS: Omit<WorldZone, "waystoneDiscovered" | "explored">[] = [
  {
    id: "ember_lane",
    name: "Ember Lane",
    description:
      "Your corner of the world. Warm lamplit streets and homes full of stories.",
    type: "residential",
    dangerLevel: 0,
    requiredLevel: 0,
    mapX: 0.5,
    mapY: 0.55,
    connections: ["hearthstone_square", "ironforge_district", "roothollow"],
    primaryColor: "#c8a050",
    secondaryColor: "#e8c890",
    icon: "home",
    defaultWeather: "clear",
    defaultMood: { warmth: 0.9, energy: 0.4, mystery: 0.15, nature: 0.25 },
  },
  {
    id: "hearthstone_square",
    name: "Hearthstone Square",
    description:
      "Where everyone gathers. The great fountain never freezes, even in winter.",
    type: "social",
    dangerLevel: 0,
    requiredLevel: 0,
    mapX: 0.45,
    mapY: 0.40,
    connections: [
      "ember_lane",
      "lantern_market",
      "verdant_park",
      "ironforge_district",
      "training_grounds",
    ],
    primaryColor: "#e8c070",
    secondaryColor: "#f8e0a0",
    icon: "fountain",
    defaultWeather: "clear",
    defaultMood: { warmth: 0.8, energy: 0.75, mystery: 0.1, nature: 0.2 },
  },
  {
    id: "lantern_market",
    name: "Lantern Market",
    description:
      "If it exists, someone here is selling it. Follow the paper lanterns.",
    type: "commercial",
    dangerLevel: 0,
    requiredLevel: 0,
    mapX: 0.60,
    mapY: 0.35,
    connections: [
      "hearthstone_square",
      "moonlit_harbor",
      "ironforge_district",
    ],
    primaryColor: "#e88040",
    secondaryColor: "#f8b060",
    icon: "lantern",
    defaultWeather: "clear",
    defaultMood: { warmth: 0.7, energy: 0.9, mystery: 0.2, nature: 0.05 },
  },
  {
    id: "verdant_park",
    name: "Verdant Park",
    description:
      "Wildflowers know no season here. The oldest tree remembers everything.",
    type: "nature",
    dangerLevel: 0,
    requiredLevel: 0,
    mapX: 0.35,
    mapY: 0.30,
    connections: [
      "hearthstone_square",
      "whisperwood",
      "skyreach_terrace",
    ],
    primaryColor: "#60a060",
    secondaryColor: "#90c070",
    icon: "leaf",
    defaultWeather: "clear",
    defaultMood: { warmth: 0.55, energy: 0.45, mystery: 0.3, nature: 0.95 },
  },
  {
    id: "moonlit_harbor",
    name: "Moonlit Harbor",
    description: "Salt air and old ropes. Ships come and go with the tides.",
    type: "harbor",
    dangerLevel: 1,
    requiredLevel: 0,
    mapX: 0.75,
    mapY: 0.50,
    connections: [
      "lantern_market",
      "ironforge_district",
      "stormwatch_cliffs",
    ],
    primaryColor: "#5080a0",
    secondaryColor: "#7ab0d0",
    icon: "anchor",
    defaultWeather: "wind",
    defaultMood: { warmth: 0.35, energy: 0.6, mystery: 0.5, nature: 0.4 },
  },
  {
    id: "ironforge_district",
    name: "Ironforge District",
    description:
      "The hammers ring from dawn to dusk. Everything worth making starts here.",
    type: "crafting",
    dangerLevel: 0,
    requiredLevel: 0,
    mapX: 0.65,
    mapY: 0.55,
    connections: [
      "ember_lane",
      "hearthstone_square",
      "lantern_market",
      "moonlit_harbor",
      "training_grounds",
    ],
    primaryColor: "#a07040",
    secondaryColor: "#c09060",
    icon: "anvil",
    defaultWeather: "clear",
    defaultMood: { warmth: 0.65, energy: 0.85, mystery: 0.1, nature: 0.05 },
  },
  {
    id: "whisperwood",
    name: "Whisperwood",
    description:
      "The trees lean close to listen. Not everything under the canopy is friendly.",
    type: "forest",
    dangerLevel: 1,
    requiredLevel: 0,
    mapX: 0.25,
    mapY: 0.45,
    connections: [
      "verdant_park",
      "ashen_peaks",
      "roothollow",
      "crystal_caverns",
    ],
    primaryColor: "#408050",
    secondaryColor: "#60a870",
    icon: "tree",
    defaultWeather: "fog",
    defaultMood: { warmth: 0.25, energy: 0.3, mystery: 0.8, nature: 0.9 },
  },
  {
    id: "ashen_peaks",
    name: "Ashen Peaks",
    description: "The air thins. The views are worth every step.",
    type: "mountain",
    dangerLevel: 2,
    requiredLevel: 0,
    mapX: 0.20,
    mapY: 0.20,
    connections: [
      "whisperwood",
      "verdant_park",
      "skyreach_terrace",
    ],
    primaryColor: "#8a7a6a",
    secondaryColor: "#b0a090",
    icon: "peak",
    defaultWeather: "wind",
    defaultMood: { warmth: 0.1, energy: 0.5, mystery: 0.55, nature: 0.7 },
  },
  {
    id: "roothollow",
    name: "Roothollow",
    description:
      "Deeper than anyone expected. The crystals grow in the silence.",
    type: "underground",
    dangerLevel: 2,
    requiredLevel: 0,
    mapX: 0.40,
    mapY: 0.65,
    connections: [
      "ember_lane",
      "whisperwood",
      "crystal_caverns",
    ],
    primaryColor: "#6a50a0",
    secondaryColor: "#9070c8",
    icon: "crystal",
    defaultWeather: "fog",
    defaultMood: { warmth: 0.2, energy: 0.2, mystery: 0.95, nature: 0.15 },
  },
  {
    id: "skyreach_terrace",
    name: "Skyreach Terrace",
    description:
      "Above the clouds, the whole world is a map. The wind has stories.",
    type: "skyline",
    dangerLevel: 1,
    requiredLevel: 0,
    mapX: 0.55,
    mapY: 0.15,
    connections: [
      "verdant_park",
      "ashen_peaks",
      "stormwatch_cliffs",
    ],
    primaryColor: "#70a0c0",
    secondaryColor: "#a0c8e0",
    icon: "cloud",
    defaultWeather: "wind",
    defaultMood: { warmth: 0.15, energy: 0.7, mystery: 0.4, nature: 0.6 },
  },
  {
    id: "crystal_caverns",
    name: "Crystal Caverns",
    description:
      "Light bends wrong here. Treasures beyond price, if you survive.",
    type: "danger",
    dangerLevel: 3,
    requiredLevel: 10,
    mapX: 0.30,
    mapY: 0.75,
    connections: [
      "roothollow",
      "whisperwood",
    ],
    primaryColor: "#a060c0",
    secondaryColor: "#c890e0",
    icon: "gem",
    defaultWeather: "fog",
    defaultMood: { warmth: 0.05, energy: 0.15, mystery: 1.0, nature: 0.1 },
  },
  {
    id: "stormwatch_cliffs",
    name: "Stormwatch Cliffs",
    description:
      "Where lightning meets the sea. Only the brave or foolish come here.",
    type: "danger",
    dangerLevel: 3,
    requiredLevel: 15,
    mapX: 0.80,
    mapY: 0.25,
    connections: [
      "moonlit_harbor",
      "skyreach_terrace",
    ],
    primaryColor: "#c05050",
    secondaryColor: "#e07878",
    icon: "storm",
    defaultWeather: "storm",
    defaultMood: { warmth: 0.0, energy: 0.95, mystery: 0.6, nature: 0.5 },
  },
  {
    id: "training_grounds",
    name: "The Proving Grounds",
    description:
      "Sand and steel. Every great warrior began here — bruised, humbled, and hungry to improve. The combat dummies don't judge.",
    type: "training",
    dangerLevel: 0,
    requiredLevel: 0,
    mapX: 0.70,
    mapY: 0.70,
    connections: [
      "hearthstone_square",
      "ironforge_district",
    ],
    primaryColor: "#c0884a",
    secondaryColor: "#e8b060",
    icon: "sword",
    defaultWeather: "clear",
    defaultMood: { warmth: 0.4, energy: 0.9, mystery: 0.1, nature: 0.15 },
  },
];

// ─── Travel Route Flavor Text ─────────────────────────────────────────────────

/**
 * Maps a "from:to" key (alphabetically sorted) to a description string.
 * Each route is bidirectional — the description is written neutrally or
 * from the perspective of traveling between these two places.
 */
const ROUTE_DESCRIPTIONS: Record<string, string> = {
  "ember_lane:hearthstone_square":
    "A short cobblestone walk lit by iron lanterns, where neighbors wave from their windows.",
  "ember_lane:ironforge_district":
    "Past the smithies' alley, where the air smells of hot metal and sawdust.",
  "ember_lane:roothollow":
    "A wooden ladder descends from behind the old miller's cellar into the earth below.",
  "hearthstone_square:lantern_market":
    "A lively promenade strung with paper lanterns, busy at all hours.",
  "hearthstone_square:verdant_park":
    "A wide gravel path flanked by flowering hedges leads up to the park gates.",
  "hearthstone_square:ironforge_district":
    "Through the tradesmen's quarter, where cart wheels rattle over old stones.",
  "ironforge_district:lantern_market":
    "A busy goods road where crates and parcels change hands at every corner.",
  "ironforge_district:moonlit_harbor":
    "A steep descending road where loaded wagons creak toward the docks.",
  "lantern_market:moonlit_harbor":
    "Downhill through the fishmongers' street, where the breeze turns salty.",
  "moonlit_harbor:stormwatch_cliffs":
    "A treacherous cliff-road where the sea crashes far below and the wind fights you.",
  "verdant_park:whisperwood":
    "Where the manicured lawn gives way to wildgrass, and the trees grow close.",
  "verdant_park:skyreach_terrace":
    "A winding switchback trail through alpine meadows that end in open sky.",
  "whisperwood:ashen_peaks":
    "The trees thin gradually until only granite and sky remain.",
  "whisperwood:roothollow":
    "An ancient hollow oak conceals a passage that smells of cold earth and old rain.",
  "whisperwood:crystal_caverns":
    "A nearly invisible trail through briar and shadow, marked only by glowing fungi.",
  "ashen_peaks:skyreach_terrace":
    "A ridge walk where one side falls away into cloud and the other into forest.",
  "roothollow:crystal_caverns":
    "Deeper still — the walls close in and the crystals multiply.",
  "skyreach_terrace:stormwatch_cliffs":
    "A sky-high traverse where the wind is strong enough to knock you sideways.",
  "hearthstone_square:training_grounds":
    "Past the memorial arch, where the cobblestones give way to packed sand and the sound of steel on wood.",
  "ironforge_district:training_grounds":
    "Through the armorer's alley, where freshly forged practice blades line the racks.",
};

/** Base travel time in seconds per danger level */
const TRAVEL_TIME_BY_DANGER: Record<number, number> = {
  0: 8,
  1: 14,
  2: 22,
  3: 35,
};

// ─── Factory ──────────────────────────────────────────────────────────────────

/** Initialize all zones with default discovery state. */
export function createWorldState(): WorldState {
  const zones: WorldZone[] = ZONE_DEFS.map(def => ({
    ...def,
    // Ember Lane starts already discovered — it's home
    waystoneDiscovered: def.id === "ember_lane",
    explored: def.id === "ember_lane" ? 1.0 : 0,
  }));

  return {
    zones,
    currentZoneId: "ember_lane",
    travelState: {
      active: false,
      fromZoneId: null,
      toZoneId: null,
      progress: 0,
      startedAt: 0,
      duration: 0,
      phase: "departing",
    },
  };
}

// ─── Zone Accessors ───────────────────────────────────────────────────────────

export function getZone(
  state: WorldState,
  id: string,
): WorldZone | undefined {
  return state.zones.find(z => z.id === id);
}

export function getConnectedZones(
  state: WorldState,
  id: string,
): WorldZone[] {
  const zone = getZone(state, id);
  if (!zone) return [];
  return zone.connections
    .map(cid => getZone(state, cid))
    .filter((z): z is WorldZone => z !== undefined);
}

// ─── Travel Access Control ────────────────────────────────────────────────────

export function canTravel(
  state: WorldState,
  toId: string,
  playerReputation: number,
): { allowed: boolean; reason?: string } {
  if (state.travelState.active) {
    return { allowed: false, reason: "Already traveling." };
  }

  const from = getZone(state, state.currentZoneId);
  const to = getZone(state, toId);

  if (!to) {
    return { allowed: false, reason: "Unknown destination." };
  }
  if (!from) {
    return { allowed: false, reason: "Current zone is unknown." };
  }
  if (toId === state.currentZoneId) {
    return { allowed: false, reason: "You are already here." };
  }

  // Check direct connection or waystone fast-travel
  const directlyConnected = from.connections.includes(toId);
  const waystoneReachable = to.waystoneDiscovered;

  if (!directlyConnected && !waystoneReachable) {
    return {
      allowed: false,
      reason: `No path to ${to.name}. Discover a waystone first.`,
    };
  }

  if (playerReputation < to.requiredLevel) {
    return {
      allowed: false,
      reason: `Requires Reputation ${to.requiredLevel}.`,
    };
  }

  return { allowed: true };
}

// ─── Travel State Machine ─────────────────────────────────────────────────────

/** Begin travel to a zone. Does not validate — call canTravel first. */
export function startTravel(state: WorldState, toId: string): void {
  const from = getZone(state, state.currentZoneId);
  const to = getZone(state, toId);
  if (!from || !to) return;

  const danger = Math.max(from.dangerLevel, to.dangerLevel);
  const baseSecs = TRAVEL_TIME_BY_DANGER[danger] ?? 10;
  const durationMs = baseSecs * 1000;

  state.travelState = {
    active: true,
    fromZoneId: state.currentZoneId,
    toZoneId: toId,
    progress: 0,
    startedAt: Date.now(),
    duration: durationMs,
    phase: "departing",
  };
}

/**
 * Tick travel progress. Call every frame with dt in seconds.
 * Returns { arrived: true, zoneId } when the player lands.
 */
export function updateTravel(
  state: WorldState,
  dt: number,
): { arrived: boolean; zoneId?: string } {
  const ts = state.travelState;
  if (!ts.active || ts.toZoneId === null) {
    return { arrived: false };
  }

  const elapsed = Date.now() - ts.startedAt;
  ts.progress = Math.min(1, elapsed / ts.duration);

  // Phase transitions
  if (ts.progress < 0.15) {
    ts.phase = "departing";
  } else if (ts.progress < 0.85) {
    ts.phase = "traveling";
  } else {
    ts.phase = "arriving";
  }

  if (ts.progress >= 1) {
    const arrivedId = ts.toZoneId;
    state.currentZoneId = arrivedId;

    // Mark zone as discovered / explored
    const arrived = getZone(state, arrivedId);
    if (arrived) {
      arrived.waystoneDiscovered = true;
      if (arrived.explored === 0) arrived.explored = 0.1;
    }

    ts.active = false;
    ts.progress = 0;
    ts.fromZoneId = null;
    ts.toZoneId = null;

    return { arrived: true, zoneId: arrivedId };
  }

  // Suppress unused dt warning — dt is used implicitly by Date.now() progression
  void dt;
  return { arrived: false };
}

// ─── Route Listing ────────────────────────────────────────────────────────────

/** All travel routes reachable from the current zone. */
export function getTravelRoutes(state: WorldState): TravelRoute[] {
  const current = getZone(state, state.currentZoneId);
  if (!current) return [];

  return current.connections.map(toId => {
    const to = getZone(state, toId);
    const danger = Math.max(current.dangerLevel, (to?.dangerLevel ?? 0));

    // Build the canonical sorted key for the lookup table
    const pair = [state.currentZoneId, toId].sort().join(":");
    const description =
      ROUTE_DESCRIPTIONS[pair] ??
      `A path between ${current.name} and ${to?.name ?? toId}.`;

    return {
      from: state.currentZoneId,
      to: toId,
      travelTime: TRAVEL_TIME_BY_DANGER[danger] ?? 10,
      dangerLevel: danger,
      description,
    };
  });
}
